// Admin-only writer for activity_scoring_config. Receives the list of
// "dirty" rows (the admin UI already filtered out unchanged rows) and
// PATCHes them through workspaceProject.activity.updateScoringConfig.
// Server enforces the superadmin gate (mongoStore.js requireSuperadmin), so
// a non-superadmin's whole batch is denied.
//
// Returns { ok, updated, rejected } — never a bare boolean. tickets/
// sonnet.md P2 (2026-08-15 bellforge owner audit): the old version only
// ever distinguished success from failure by whether the SDK call THREW —
// a per-row server-side rejection (score out of the 0–100 UI's own bound,
// or a stale activity_type) resolves normally with `{ok:false, rejected:
// [...]}` in the body, which used to read as success here, so the admin
// saw "All changes saved." for a write that silently discarded exactly the
// row it should have complained about. The server now reports real,
// per-row {activity_type, reason} rejections (mongoStore.js
// scopedUpdateScoringConfig) instead of a blind {ok:true} — this function's
// job is to pass that through unmangled, not to collapse it back into a
// boolean.
//
// migrated 2026-04-27 to SDK route workspaceProject.activity.updateScoringConfig

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// `rejected[].reason` is the server's own message when it sent one, else a bundle
// KEY (`admin.activityScoring.reason.*`) — the caller resolves it through polyglot,
// which returns a server message verbatim.
export const updateActivityScoring = async function (dirty) {
  if (!Array.isArray(dirty) || !dirty.length) return { ok: true, updated: [], rejected: [] }
  const me = this?.state?.root?.currentUserEmail || null
  const now = new Date().toISOString()
  const rows = dirty.map((r) => ({
    activity_type: r.activity_type,
    enabled: r._draftEnabled,
    score: r._draftScore,
    updated_at: now,
    updated_by: me
  }))
  try {
    const res = await shellGetSDK().execute('workspaceProject.activity', 'updateScoringConfig', { rows })
    const data = res && typeof res === 'object' && 'data' in res ? res.data : res
    // A denied (non-superadmin) call resolves with the server's {status,
    // error} envelope, not the {ok,updated,rejected} shape above — surface
    // it as a single rejection naming every row rather than pretending one
    // of the two known-good shapes was returned.
    if (!data || typeof data !== 'object' || !Array.isArray(data.rejected)) {
      return {
        ok: false,
        updated: [],
        rejected: rows.map((r) => ({
          activity_type: r.activity_type,
          reason: data?.error?.message || data?.message || 'admin.activityScoring.reason.rejected'
        }))
      }
    }
    return { ok: data.ok !== false, updated: data.updated || [], rejected: data.rejected }
  } catch (err) {
    console.error('[updateActivityScoring]', err?.message || err)
    return {
      ok: false,
      updated: [],
      rejected: rows.map((r) => ({
        activity_type: r.activity_type,
        reason: err?.message || 'admin.activityScoring.reason.network'
      }))
    }
  }
}
